// Simple authentication utilities with localStorage persistence

const AUTH_KEY = 'reading_log_auth';
const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000; // 90 days in milliseconds

interface AuthData {
  username: string;
  isAuthenticated: boolean;
  expiresAt: number;
}

// Simple username/password validation (replace with your actual credentials)
const VALID_CREDENTIALS = {
  username: 'lennypeng', // Change this to your desired username
  password: '15dyl16y' // Change this to your desired password
};

export const authUtils = {
  // Check if user is currently authenticated
  isAuthenticated(): boolean {
    try {
      const authData = localStorage.getItem(AUTH_KEY);
      if (!authData) return false;

      const parsed: AuthData = JSON.parse(authData);
      const now = Date.now();

      // Check if the authentication has expired
      if (now > parsed.expiresAt) {
        this.logout();
        return false;
      }

      return parsed.isAuthenticated;
    } catch (error) {
      console.error('Error checking authentication:', error);
      return false;
    }
  },

  // Get current authenticated user
  getUser(): string | null {
    try {
      const authData = localStorage.getItem(AUTH_KEY);
      if (!authData) return null;

      const parsed: AuthData = JSON.parse(authData);
      return this.isAuthenticated() ? parsed.username : null;
    } catch (error) {
      console.error('Error getting user:', error);
      return null;
    }
  },

  // Attempt to login with username and password
  async login(username: string, password: string, rememberMe: boolean = true): Promise<boolean> {
    // Simple credential validation (replace with your actual authentication logic)
    if (username.trim() === VALID_CREDENTIALS.username && password === VALID_CREDENTIALS.password) {
      if (rememberMe) {
        const expiresAt = Date.now() + NINETY_DAYS_MS;
        const authData: AuthData = {
          username: username.trim(),
          isAuthenticated: true,
          expiresAt
        };
        localStorage.setItem(AUTH_KEY, JSON.stringify(authData));
      }
      return true;
    }
    return false;
  },

  // Logout and clear authentication data
  logout(): void {
    localStorage.removeItem(AUTH_KEY);
  },

  // Check if authentication will expire soon (within 7 days)
  willExpireSoon(): boolean {
    try {
      const authData = localStorage.getItem(AUTH_KEY);
      if (!authData) return false;

      const parsed: AuthData = JSON.parse(authData);
      const now = Date.now();
      const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;

      return (parsed.expiresAt - now) < sevenDaysMs;
    } catch (error) {
      return false;
    }
  },

  // Extend the current session by another 90 days
  extendSession(): boolean {
    try {
      const authData = localStorage.getItem(AUTH_KEY);
      if (!authData) return false;

      const parsed: AuthData = JSON.parse(authData);
      if (!this.isAuthenticated()) return false;

      parsed.expiresAt = Date.now() + NINETY_DAYS_MS;
      localStorage.setItem(AUTH_KEY, JSON.stringify(parsed));
      return true;
    } catch (error) {
      console.error('Error extending session:', error);
      return false;
    }
  }
};

// Export the credentials for easy modification
export const updateCredentials = (username: string, password: string) => {
  VALID_CREDENTIALS.username = username;
  VALID_CREDENTIALS.password = password;
};